import { useCallback, useEffect, useMemo, useState } from 'react'
import Avatar from 'react-avatar'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Swal from 'sweetalert2'

import { TApi, TRoles, TSupporterStatus, StateOptions } from '../../../../shared/const'
import http from '../../../../shared/http'
import {
	downloadFile,
	escapeHtml,
	invitationTone,
	invitationValidity,
	stateLabel
} from '../../../../shared/helpers'
import { formatVienna } from '../../../../shared/datetime'
import { isAlumni } from '../../../../shared/alumni'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Table from '../../../../UI/Table'
import Pagination from '../../../../UI/Pagination'
import Search from '../../../../UI/Search'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	competenceField,
	industryField,
	schoolTypeField,
	stateField,
	supporterCompanyField,
	supporterRoleField,
	supporterStatusField
} from '../../../../UI/FilterPanel/fields'
import BtnTooltip from '../../../../UI/BtnTooltip'
import ReportsCard from '../../../../UI/ReportsCard'
import InviteSupporterModal from './InviteSupporterModal'
import EditSupporterModal from './EditSupporterModal'
import EditInvitationModal from './EditInvitationModal'

import styles from './style.module.scss'
import AuthAvatar from '../../../../UI/AuthAvatar'

const LIMIT = 10

/**
 * Admin-Liste der Unterstuetzer:innen.
 *
 * Loest die frueheren "Wirtschaftsexperten" ab. Anders als dort ist eine
 * Unterstuetzer:in ein echter User mit Login und kann mehrere Sub-Rollen
 * gleichzeitig haben - deshalb Rollen-Badges statt einer einzelnen Spalte.
 */
function Supporters() {
	const { t, i18n } = useTranslation()

	// Die Branchen stehen als { value_EN, value_DE } im Store: gespeichert ist
	// die englische Fassung, angezeigt gehoert die deutsche.
	const branches = useSelector(state => state.options?.branch) || []

	/*
	 * Die Freigabe einer Selbstregistrierung liegt bei der Bundesebene
	 * (Anforderung #58). Das Backend weist einen Landesadmin ab; hier bleibt die
	 * Handlung deshalb gar nicht erst stehen — ein Knopf, der immer 403 liefert,
	 * ist schlechter als keiner.
	 */
	const role = useSelector(state => state.user?.role)
	const canApprove = role === TRoles.FEDERAL_ADMIN
	/*
	 * Alumni (#213) lesen die Liste und die Profile mit Kontaktdaten — aber
	 * ohne Einladen, Bearbeiten, Löschen und Herunterladen. Eine Einladung ist
	 * für sie kein Datensatz zum Öffnen: Ihr Fenster ist ein Bearbeitungsformular.
	 */
	const readOnly = isAlumni(role)
	const canOpen = row =>
		row.availabilityStatus !== TSupporterStatus.DELETED &&
		!(readOnly && row.availabilityStatus === TSupporterStatus.INVITED)

	const [rows, setRows] = useState([])
	const [count, setCount] = useState(0)
	const [statusCounts, setStatusCounts] = useState({})
	const [page, setPage] = useState(0)
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const [isLoading, setLoading] = useState(false)
	const [showInvite, setShowInvite] = useState(false)
	const [editId, setEditId] = useState(null)
	const [editInvitationId, setEditInvitationId] = useState(null)
	const [lookups, setLookups] = useState({
		industries: [],
		schoolTypes: [],
		competenceGroups: [],
		companies: []
	})

	// Dieselbe Leiste wie in den uebrigen Listen: die Felder kommen aus
	// FilterPanel/fields, die Ansicht sagt nur, welche sie braucht.
	const fields = useMemo(
		() => [
			supporterRoleField(t),
			// Unternehmen weit oben (Anforderung #185): Die Liste der Partner ist
			// lang, und als letztes Feld ging sie am unteren Rand der Leiste unter.
			supporterCompanyField(t, lookups.companies),
			stateField(t),
			supporterStatusField(t),
			industryField(t, lookups.industries, branches, i18n.language),
			schoolTypeField(t, lookups.schoolTypes),
			competenceField(t, lookups.competenceGroups)
		],
		[t, lookups, branches, i18n.language]
	)

	const filters = useFilters({ storageKey: 'supporters', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchSupporters = useCallback(() => {
		const isCurrent = latest()

		setLoading(true)
		http.get(TApi.ADMIN_SUPPORTERS, {
			params: {
				limit: LIMIT,
				offset: page * LIMIT,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		})
			.then(({ data }) => {
				if (!isCurrent()) return
				setRows(data.rows || [])
				setCount(data.count || 0)
				setStatusCounts(data.statusCounts || {})
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => isCurrent() && setLoading(false))
		// t bewusst NICHT in den Abhaengigkeiten: useTranslation liefert bei
		// jedem Render eine neue Funktionsreferenz. Mit t in der Liste entstuende
		// eine Endlosschleife Render -> Fetch -> Render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, debouncedSearch, filterQuery])

	useEffect(() => {
		fetchSupporters()
	}, [fetchSupporters])

	useEffect(() => {
		http.get(TApi.ADMIN_SUPPORTER_LOOKUPS)
			.then(({ data }) => setLookups(data))
			.catch(() => {
				// Stammdaten sind nur fuer die Filter noetig - ohne sie bleibt die
				// Liste bedienbar, deshalb kein Fehler-Toast.
			})
	}, [])

	/*
	 * Freigabe einer Selbstregistrierung.
	 *
	 * Junior Achievement prueft nach einer kurzen Recherche zur Person im Netz
	 * (Anforderung #58). Das ist eine Ermessensentscheidung und laesst sich nicht
	 * als Regel hinterlegen — die Rueckfrage sagt deshalb, worauf zu achten ist,
	 * und legt die Suche nach Name und Unternehmen gleich bereit. Ohne diesen
	 * Zwischenschritt gaebe ein Klick im Zeilenmenue das Profil sofort frei.
	 */
	const approve = async supporter => {
		const query = [supporter.displayName, supporter.company].filter(Boolean).join(' ')
		const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`

		const result = await Swal.fire({
			title: t('approveSupporterTitle'),
			html:
				`<b>${escapeHtml(supporter.displayName)}</b>` +
				(supporter.company ? ` · ${escapeHtml(supporter.company)}` : '') +
				(supporter.email ? `<br/><span style="color:#777">${escapeHtml(supporter.email)}</span>` : '') +
				`<br/><br/>${escapeHtml(t('approveSupporterHint'))}` +
				(query
					? `<br/><br/><a href="${searchUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(
							t('approveSupporterSearch')
					  )}</a>`
					: ''),
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: t('approveSupporter'),
			cancelButtonText: t('Cancel')
		})

		if (!result.isConfirmed) return

		http.post(TApi.ADMIN_SUPPORTER_APPROVE(supporter.id))
			.then(() => {
				toast.success(t('Saved successfully'))
				fetchSupporters()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	const remove = async row => {
		// Eine Einladung wird zurückgezogen (eigener Endpunkt), ein Profil gelöscht.
		const isInvited = row.availabilityStatus === TSupporterStatus.INVITED
		/*
		 * Eine noch nicht freigegebene Selbstregistrierung ist der Weg, ein
		 * erfundenes Konto loszuwerden (Anforderung #58) — und dabei geht
		 * ausdruecklich keine Nachricht hinaus. Der Text muss das sagen, sonst
		 * rechnet die Verwaltung mit einer Absage an die Person.
		 */
		const isPending = row.availabilityStatus === TSupporterStatus.PENDING_APPROVAL

		const deleteLabel = isPending ? t('deletePendingProfile') : t('deleteProfile')

		const result = await Swal.fire({
			title: isInvited ? t('cancelInvitation') : deleteLabel,
			text: isInvited
				? t('cancelInvitationHint')
				: isPending
				? t('deletePendingProfileHint')
				: t('deleteProfileHint'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: isInvited ? t('cancelInvitation') : deleteLabel,
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#d33'
		})

		if (!result.isConfirmed) return

		const request = isInvited
			? http.delete(TApi.ADMIN_SUPPORTER_INVITATION_ID(row.invitationId))
			: http.delete(TApi.ADMIN_SUPPORTER_ID(row.id))

		request
			.then(() => {
				toast.success(t('Saved successfully'))
				// Das geloeschte Profil kann nicht offen bleiben.
				setEditId(null)
				fetchSupporters()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	/**
	 * Verschickt eine offene Einladung noch einmal.
	 *
	 * Der bisherige Aktivierungslink verliert dabei seine Gueltigkeit und die
	 * Frist beginnt neu - deshalb wird die Liste danach neu geholt, sonst stuende
	 * dort weiter das alte Einladungsdatum.
	 */
	const resendInvitation = row => {
		http.post(TApi.ADMIN_SUPPORTER_INVITATION_RESEND(row.invitationId))
			.then(({ data }) => {
				if (data?.emailSent === false) {
					toast.warning(t('invitationResentButEmailFailed'))
				} else {
					toast.success(t('Invitation resent successfully'))
				}
				fetchSupporters()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	/**
	 * Oeffnet den Datensatz hinter einer Zeile.
	 *
	 * Eine Einladung hat noch kein Profil - dort fuehrt der Klick in die
	 * Einladungsmaske, sonst ins Profil. Geloeschte (anonymisierte) Profile
	 * lassen sich nicht mehr oeffnen, es gibt nichts mehr zu sehen.
	 */
	const openRow = row => {
		if (!canOpen(row)) return
		if (row.availabilityStatus === TSupporterStatus.INVITED) {
			setEditInvitationId(row.invitationId)
			return
		}
		setEditId(row.id)
	}

	const download = () => {
		setLoading(true)
		http.get(TApi.ADMIN_SUPPORTER_DOWNLOAD, {
			responseType: 'arraybuffer',
			params: { search: debouncedSearch, filter: filterQuery }
		})
			.then(({ data }) => {
				downloadFile(
					'Unterstuetzer.xlsx',
					new Blob([data], {
						type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
					})
				)
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setLoading(false))
	}

	const columns = useMemo(
		() => [
			{
				Header: t('name'),
				accessor: 'displayName',
				width: 220,
				Cell: ({ row }) => {
					// Geoeffnet wird ueber die ganze Zeile (onRowClick), wie in den
					// uebrigen Listen auch.
					return (
						<div className={styles.nameCell}>
							<AuthAvatar
								hashName={row.original.avatarHashName}
								className={styles.avatar}
								fallback={<Avatar name={row.original.displayName || '?'} size="32" round />}
							/>
							<div className={styles.nameText}>
								<span className={styles.name}>{row.original.displayName}</span>
								<span className={styles.company}>
									{row.original.company || row.original.email}
								</span>
							</div>
						</div>
					)
				}
			},
			{
				/*
				 * Gezeigt werden die Bundeslaender, fuer die jemand zur Verfuegung
				 * steht — nicht das Heimat-Bundesland. Das Heimat-Bundesland ist bei
				 * Unterstuetzer:innen meist gar nicht gepflegt, die Spalte stand
				 * deshalb durchgehend auf "—". Gefiltert wird ohnehin ueber beides.
				 */
				Header: t('states'),
				accessor: 'targetStates',
				width: 170,
				disableSortBy: true,
				Cell: ({ row }) => {
					const target = row.original.targetStates || []
					const home = row.original.state

					// Alle neun ausgewaehlt heisst "ueberall" - neun Kuerzel
					// nebeneinander sagen dasselbe, nur unleserlich.
					if (target.length >= StateOptions.length) {
						return <span className={styles.tag}>{t('allStates')}</span>
					}

					if (!target.length) {
						return home ? (
							<span className={styles.tag} title={t('homeState')}>
								{stateLabel(home)}
							</span>
						) : (
							<span className={styles.muted}>—</span>
						)
					}

					return (
						<div className={styles.tags} title={target.map(stateLabel).join(', ')}>
							{target.slice(0, 3).map(state => (
								<span key={state} className={styles.tag}>
									{stateLabel(state)}
								</span>
							))}
							{target.length > 3 && <span className={styles.tag}>+{target.length - 3}</span>}
						</div>
					)
				}
			},
			{
				Header: t('roles'),
				accessor: 'roles',
				width: 170,
				disableSortBy: true,
				Cell: ({ value = [] }) => (
					<div className={styles.badges}>
						{value.map(role => (
							<span key={role} className={clsx(styles.badge, styles[`badge${role}`])}>
								{t(`supporterRole${role}`)}
							</span>
						))}
					</div>
				)
			},
			{
				Header: t('competences'),
				accessor: 'competences',
				width: 220,
				disableSortBy: true,
				Cell: ({ value = [] }) => (
					<div className={styles.tags}>
						{value.slice(0, 3).map(item => (
							<span key={item} className={styles.tag}>
								{item}
							</span>
						))}
						{value.length > 3 && <span className={styles.tag}>+{value.length - 3}</span>}
					</div>
				)
			},
			{
				Header: t('offers'),
				accessor: 'offersCount',
				width: 80,
				disableSortBy: true
			},
			{
				// Spec Teil 2 B.1: verbundene Junior Companies gehoeren in die
				// Uebersicht. Angefragte und verbundene werden getrennt gezeigt,
				// weil das der Unterschied ist, der den Admin interessiert.
				Header: t('connectedCompanies'),
				accessor: 'connections',
				width: 140,
				disableSortBy: true,
				Cell: ({ value = [] }) => {
					const connected = value.filter(c => c.status === 'CONNECTED').length
					const requested = value.filter(c => c.status === 'REQUESTED').length

					if (!connected && !requested) return <span className={styles.muted}>—</span>

					return (
						<span className={styles.connectionCount}>
							{connected > 0 && <strong>{connected}</strong>}
							{requested > 0 && (
								<span className={styles.requested}>
									+{requested} {t('connectionStatusREQUESTED').toLowerCase()}
								</span>
							)}
						</span>
					)
				}
			},
			{
				/*
				 * Wann eingeladen, wie lange der Link noch gilt und wie oft schon
				 * erinnert wurde. Sortiert nach dieser Spalte stehen die ältesten
				 * Einladungen - die bald ablaufen - oben. Profile tragen kein
				 * Einladungsdatum mehr.
				 */
				Header: t('invitedOnColumn'),
				accessor: 'invitedAt',
				width: 150,
				Cell: ({ row }) => {
					const supporter = row.original
					if (supporter.availabilityStatus !== TSupporterStatus.INVITED || !supporter.invitedAt) {
						return <span className={styles.muted}>—</span>
					}

					const tone = invitationTone(supporter.invitedAt, supporter.validDays)
					const validity = invitationValidity(supporter.invitedAt, supporter.validDays)
					const reminders = supporter.reminderCount || 0

					return (
						<span className={styles.invited}>
							<span className={styles.invitedDate}>{formatVienna(supporter.invitedAt, 'dd.MM.yyyy')}</span>
							<span className={clsx(styles.invitedTone, styles[`tone_${tone}`])}>
								<span className={styles.toneDot} />
								{validity.expired
									? t('invitationExpiredShort')
									: // Einzahl/Mehrzahl selbst: Die Sprache heißt intern „ger",
									  // dafür kennt i18next keine Pluralregel.
									  t(validity.daysLeft === 1 ? 'invitationDayLeft' : 'invitationDaysLeft', {
											count: validity.daysLeft
									  })}
							</span>
							{reminders > 0 && (
								<span className={styles.invitedReminders}>
									{t('invitationRemindedTimes', { count: reminders })}
								</span>
							)}
						</span>
					)
				}
			},
			{
				Header: t('status'),
				accessor: 'availabilityStatus',
				width: 150,
				Cell: ({ value }) => (
					<span className={clsx(styles.status, styles[`status${value}`])}>
						<span className={styles.statusDot} />
						{t(`supporterStatus${value}`)}
					</span>
				)
			},
			{
				/*
				 * Die Handlungen liegen im Drei-Punkte-Menue statt als Knopfreihe.
				 * Nebeneinander gestellt waren sie so breit, dass die letzte am
				 * rechten Rand abgeschnitten wurde — und sie wachsen mit jeder
				 * weiteren Handlung und in jeder Sprache weiter.
				 */
				Header: '',
				accessor: 'id',
				width: 70,
				disableSortBy: true,
				Cell: ({ row }) => {
					const supporter = row.original
					const isInvited = supporter.availabilityStatus === TSupporterStatus.INVITED
					const isDeleted = supporter.availabilityStatus === TSupporterStatus.DELETED

					const items = []

					// Alumni: kein Menü — Öffnen erledigt die Zeile.
					if (readOnly) return null

					// Freigeben darf nur die Bundesebene (Anforderung #58).
					if (supporter.availabilityStatus === TSupporterStatus.PENDING_APPROVAL && canApprove) {
						items.push({ label: t('approveSupporter'), onClick: () => approve(supporter) })
					}

					// Eine Einladung hat noch kein Profil, wohl aber Stammdaten
					// (Name, Adresse, Rollenvorschlag) - die muessen sich
					// korrigieren lassen, etwa bei einem Tippfehler.
					if (!isDeleted) {
						items.push({ label: t('openRecord'), onClick: () => openRow(supporter) })
						// Eine Einladung, die niemand angenommen hat, laeuft nach 60
						// Tagen ab. Ohne "erneut senden" blieb nur zuruecknehmen und
						// von vorn beginnen - die Schnittstelle dafuer gab es laengst.
						if (isInvited) {
							items.push({
								label: t('Resend invitation'),
								onClick: () => resendInvitation(supporter)
							})
						}
						items.push({
							label: isInvited ? t('cancelInvitation') : t('delete'),
							className: styles.menuDanger,
							onClick: () => remove(supporter)
						})
					}

					// Ein geloeschtes Profil ist anonymisiert - es gibt nichts mehr
					// zu tun, und ein leeres Menue waere eine Einladung zum Klicken.
					if (!items.length) return null

					return <BtnTooltip className={styles.rowMenu} items={items} />
				}
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, canApprove, readOnly]
	)

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die
	// Seitenzahl zurueck, sonst stuende man auf Seite 4 einer Liste, die nur
	// noch zwei Seiten hat.
	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	/*
	 * Kacheln je Status: Kurzwahl auf dasselbe Feld wie in der Filterleiste.
	 * Ein Klick filtert auf genau diesen Status, ein zweiter Klick hebt es auf;
	 * "Alle" nimmt den Statusfilter weg. Die Zahlen zaehlt der Server mit allen
	 * uebrigen Filtern, aber ohne den Status selbst - sonst stuende nach dem
	 * ersten Klick ueberall sonst 0.
	 *
	 * Eingeladene erscheinen nur in der Bundessicht (Einladungen tragen kein
	 * Bundesland) und nicht fuer Alumni, die die Zeilen ohnehin nicht sehen.
	 */
	const tileStatuses = [
		...(canApprove ? [TSupporterStatus.INVITED] : []),
		TSupporterStatus.PENDING_APPROVAL,
		TSupporterStatus.ACTIVE,
		TSupporterStatus.UNAVAILABLE,
		TSupporterStatus.ARCHIVED
	]
	const selectedStatus = (filters.values.availabilityStatus || []).map(item => item.value)
	const activeStatus = selectedStatus.length === 1 ? selectedStatus[0] : null
	const tileTotal = tileStatuses.reduce((sum, status) => sum + (statusCounts[status] || 0), 0)

	const handleStatusTile = status => {
		const next = !status || activeStatus === status ? [] : [status]
		handleFilterChange(
			'availabilityStatus',
			next.map(value => ({ value, label: t(`supporterStatus${value}`) }))
		)
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('supporters')} bg="#f8faff">
				<div className={styles.headerContent}>
					{/* Ganz rechts der Filter, davor die Suche - so in jeder Ansicht.
					    Handlungen der Seite reihen sich davor ein. */}
					{!readOnly && <Btn onClick={() => setShowInvite(true)}>{t('inviteSupporter')}</Btn>}
					<Search
						value={search}
						onChange={e => handleSearchChange(e.target.value)}
						placeholder={t('Find a user')}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter
						icon="arrow"
						onClick={() => setShowFilter(true)}
						count={filters.count}
					>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>

			<div className={styles.statusTiles}>
				<ReportsCard
					title={t('supporterStatusAll')}
					value={tileTotal}
					onClick={() => handleStatusTile(null)}
					active={!selectedStatus.length}
					actionLabel={t('supporterStatusTileHint')}
				/>
				{tileStatuses.map(status => (
					<ReportsCard
						key={status}
						title={t(`supporterStatus${status}`)}
						value={statusCounts[status] || 0}
						onClick={() => handleStatusTile(status)}
						active={activeStatus === status}
						actionLabel={t('supporterStatusTileHint')}
					/>
				))}
			</div>

			<div className={styles.subHeader}>
				<Pagination
					limit={LIMIT}
					page={page}
					label={t('supporters')}
					countAll={count}
					onChange={setPage}
				/>
				{!readOnly && (
					<Btn theme="download" icon="download" onClick={download} disabled={isLoading}>
						XLS
					</Btn>
				)}
			</div>

			<div className={clsx('container', styles.tableContainer)}>
				{!isLoading && !rows.length ? (
					<div className={styles.empty}>{t('noSupportersFound')}</div>
				) : (
					<Table
						columns={columns}
						handleSort={(name, direction) => setSort({ name, direction })}
						items={rows}
						limit={LIMIT}
						countAll={count}
						onChange={setPage}
						page={page}
						onRowClick={openRow}
						// Ein geloeschtes Profil ist anonymisiert - es gibt nichts
						// mehr zu oeffnen.
						isRowClickable={canOpen}
						rowTitle={t('openRecord')}
					/>
				)}
			</div>

			<EditSupporterModal
				supporterId={editId}
				lookups={lookups}
				onDelete={readOnly ? undefined : remove}
				onClose={() => setEditId(null)}
				onSaved={(options = {}) => {
					if (!options.keepOpen) setEditId(null)
					fetchSupporters()
				}}
			/>

			<EditInvitationModal
				invitationId={editInvitationId}
				onClose={() => setEditInvitationId(null)}
				onSaved={() => {
					setEditInvitationId(null)
					fetchSupporters()
				}}
				onRemoved={() => {
					// Die zurueckgezogene Einladung kann nicht offen bleiben.
					setEditInvitationId(null)
					fetchSupporters()
				}}
			/>

			<InviteSupporterModal
				show={showInvite}
				lookups={lookups}
				onClose={() => setShowInvite(false)}
				onInvited={() => {
					setShowInvite(false)
					fetchSupporters()
				}}
			/>

			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

export default Supporters
